import PrimaryHint from '@components/atoms/buttons/PrimaryHint';
import { SectionWrapperNarrow } from '@components/atoms/wrappers/SectionWrapper';
import BasicListField from '@components/organisms/BasicListField';
import DividendPeriodField from '@components/organisms/dividends/DividendPeriodField';
import NextPayoutDateLabel from '@components/organisms/portfolio/NextPayoutDateLabel';
import LotRow from '@components/organisms/tables/lots/LotRow';
import { usePortfolio } from '@hooks/api';
import { useLoadingModal } from '@hooks/useLoadingModal';
import { useScreenName } from '@hooks/useScreenName';
import { useTitle } from '@hooks/useTitle';
import { Lot } from '@models/api/user/portfolio';
import {
    ButtonLabel,
    InfoList,
    InfoListItem,
    TitleAndSubtitle,
} from '@moreharvest/component-library';
import { ctaClick, tooltipView } from '@services/analytics';
import { AnalyticsContentId, AnalyticsCtaId, toJsArray } from 'mh-client';
import type * as MH from 'mh-client';
import Oops from '@pages/error/Oops';
import { USDFormatter } from '@utils/currency';
import { FormattedMessage } from '@utils/i18n';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router';

const InvestmentActivity = () => {
    useTitle('investment_activity', 'investmentActivity');
    const { ticker } = useParams();
    const navigate = useNavigate();
    const screenName = useScreenName();

    const { data, isLoading, isError } = usePortfolio(ticker!);

    useLoadingModal(isLoading);

    if (isError) {
        return <Oops />;
    }

    if (!data) {
        return null;
    }

    const lots: Lot[] = data.lots
        ? toJsArray(data.lots).map(lot => ({
              shares: lot.shares,
              date: new Date(lot.date.value),
              fundName: lot.ticker,
              maturationDate: new Date(lot.maturationDate.value),
              isMatured: lot.isMatured,
              costBasis: lot.costBasis ?? 0,
              gainLoss: lot.gainLoss ?? 0,
          }))
        : [];

    return (
        <SectionWrapperNarrow className="!gap-y-8">
            <TitleAndSubtitle
                size="medium"
                title={<FormattedMessage id="investment_activity" />}
                subtitle={
                    <FormattedMessage id="_fund" values={{ 0: ticker }} />
                }
            />
            <ul>
                <InfoListItem.WithBox>
                    <p className="Body-Medium">
                        <FormattedMessage id="invest_act_current_share_price_usd" />
                    </p>
                    <p className="Body-Medium semibold">
                        {USDFormatter.format(data.sharePrice)}
                    </p>
                </InfoListItem.WithBox>
            </ul>
            <div className="flex gap-x-4 w-full">
                {data.totalShares > 0 && (
                    <ButtonLabel
                        size="medium"
                        variant="outline"
                        type="button"
                        id="investmentActivity_CTA_sell"
                        onClick={() => {
                            if (screenName) {
                                ctaClick({
                                    screen_name: screenName,
                                    cta_id: AnalyticsCtaId.getInstance().SELL,
                                });
                            }
                            navigate(`/funds/${ticker}/investment/sell`);
                        }}
                    >
                        <FormattedMessage id="transaction_sell" />
                    </ButtonLabel>
                )}
                <ButtonLabel
                    size="medium"
                    variant="solid"
                    fullWidth
                    type="button"
                    onClick={() => {
                        if (screenName) {
                            ctaClick({
                                screen_name: screenName,
                                cta_id: AnalyticsCtaId.getInstance().BUY,
                            });
                        }
                        navigate(`/funds/${ticker}/investment/buy`);
                    }}
                    id="investmentActivity_CTA_buy"
                >
                    <FormattedMessage id="transaction_buy" />
                </ButtonLabel>
            </div>
            <TitleAndSubtitle
                title={<FormattedMessage id="invest_act_position_details" />}
                size="small"
            />
            <InfoList boxed>
                <BasicListField
                    i18nId="invest_act_shares_owned"
                    value={`${data.totalShares}`}
                />
                <BasicListField
                    i18nId="invest_act_total_value_usd"
                    value={USDFormatter.format(data?.totalValue ?? 0)}
                />
                {!!data.nextDivEst && (
                    <InfoListItem.WithDivider>
                        <span className="Body-Medium">
                            <PrimaryHint
                                title={
                                    <FormattedMessage
                                        id="invest_act_estimated_income_usd"
                                        values={{ 0: new Date().getFullYear() }}
                                    />
                                }
                                body={
                                    <FormattedMessage id="invest_act_estimated_income_usd_body" />
                                }
                                id="investmentActivity_click_estimateDividend"
                                onClick={() =>
                                    tooltipView(
                                        screenName,
                                        AnalyticsContentId.getInstance()
                                            .ESTIMATE_DIVIDEND
                                    )
                                }
                            >
                                <FormattedMessage
                                    id="invest_act_estimated_income_usd"
                                    values={{ 0: new Date().getFullYear() }}
                                />
                            </PrimaryHint>
                        </span>
                        <strong className="Body-Medium font-semibold">
                            {USDFormatter.format(data?.nextDivEst)}
                        </strong>
                    </InfoListItem.WithDivider>
                )}
                <InfoListItem.WithDivider>
                    <span className="Body-Medium">
                        <FormattedMessage id="portfolio_next_payout_date" />
                    </span>
                    <NextPayoutDateLabel
                        nextPayoutDate={
                            data.nextPayDate
                                ? new Date(data.nextPayDate.value)
                                : undefined
                        }
                        isEligibleForDividend={data.isEligibleForDividend}
                        onTooltipClick={() =>
                            tooltipView(
                                screenName,
                                AnalyticsContentId.getInstance()
                                    .NEXT_PAYOUT_DATE
                            )
                        }
                    />
                </InfoListItem.WithDivider>
                <BasicListField
                    i18nId="invest_act_all_time_dividend_return_usd"
                    value={USDFormatter.format(data?.divAllTime ?? 0)}
                />
            </InfoList>
            {lots.length > 0 && <LotsSection data={lots} />}
            <DividendsSection data={data} />
        </SectionWrapperNarrow>
    );
};

export default InvestmentActivity;

interface LotsProps {
    data: Lot[];
}

const LotsSection = ({ data }: LotsProps) => {
    return (
        <>
            <TitleAndSubtitle
                title={<FormattedMessage id="invest_act_my_lots" />}
                subtitle={<FormattedMessage id="invest_act_subtitle" />}
                size="small"
            />
            <ul className="flex flex-col gap-y-4">
                {data.map((lot, idx) => (
                    <li
                        key={`lot-${dayjs(lot.date).format('YYYY-MM-DD')}-${idx}`}
                    >
                        <LotRow cytag={`lot-maturation-date-${idx}`} {...lot} />
                    </li>
                ))}
            </ul>
        </>
    );
};

const DividendsSection = ({ data }: { data: MH.Portfolio }) => {
    const periods = data.dividends ? toJsArray(data.dividends) : [];

    if (periods.length === 0) return null;

    return (
        <>
            <TitleAndSubtitle
                title={<FormattedMessage id="dividends" />}
                subtitle={
                    <FormattedMessage id="invest_act_dividend_payout_details" />
                }
                size="small"
            />
            <ul className="flex w-full flex-col gap-y-4">
                {periods.map((period, i) => (
                    <DividendPeriodField
                        period={period}
                        key={`${period.exDividendDate.value}-${i}`}
                    />
                ))}
            </ul>
        </>
    );
};
